import { Injectable, OnDestroy } from '@angular/core';
import { EventService, QueryNotifier, QueryState } from '@spartacus/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, iif, isObservable, merge, of, using } from 'rxjs';
import { catchError, distinctUntilChanged, map, share, switchMap, takeUntil, tap } from 'rxjs/operators';

export interface Query<T, P extends unknown[] = []> {
  get(...params: P): Observable<T | undefined>;
  getState(...params: P): Observable<QueryState<T>>;
}

// Сервис взят из спартакуса как есть после апдейта rxjs до 7 версии
// В 7 версии функция iff ожидает 3 аргумента, а не 2 как в 6 версии
@Injectable({
  providedIn: 'root',
})
export class QueryService implements OnDestroy {
  protected subscriptions = new Subscription();

  constructor(protected eventService: EventService) {}

  create<T>(
    loaderFactory: () => Observable<T>,
    options?: {
      reloadOn?: QueryNotifier[];
      resetOn?: QueryNotifier[];
    },
  ): Query<T> {
    const initialState: QueryState<T> = {
      data: undefined,
      error: false,
      loading: true,
    };

    const state$ = new BehaviorSubject<QueryState<T>>(initialState);

    // if the query will be unsubscribed from while the data is being loaded, we will end up with the loading flag set to true
    // we want to retry this load on next subscription
    const onSubscribeLoad$ = iif(() => state$.value.loading, of(undefined), EMPTY);

    const loadTrigger$ = this.getTriggersStream([
      onSubscribeLoad$, // we need to evaluate onSubscribeLoad$ before other triggers in order to avoid other triggers changing state$ value
      ...(options?.reloadOn ?? []),
      ...(options?.resetOn ?? []),
    ]);

    const resetTrigger$ = this.getTriggersStream(options?.resetOn ?? []);
    const reloadTrigger$ = this.getTriggersStream(options?.reloadOn ?? []);

    const load$ = loadTrigger$.pipe(
      tap(() => {
        if (!state$.value.loading) {
          state$.next({ ...state$.value, loading: true });
        }
      }),
      switchMap(() => loaderFactory().pipe(takeUntil(resetTrigger$))),
      tap((data) => {
        state$.next({ loading: false, error: false, data });
      }),
      catchError((error, retryStream$) => {
        state$.next({ loading: false, error, data: undefined });

        return retryStream$;
      }),
      share(),
    );

    // reload logic
    if (options?.reloadOn?.length) {
      this.subscriptions.add(
        reloadTrigger$.subscribe(() => {
          if (!state$.value.loading) {
            state$.next({ ...state$.value, loading: true });
          }
        }),
      );
    }

    // reset logic
    if (options?.resetOn?.length) {
      this.subscriptions.add(
        resetTrigger$.subscribe(() => {
          if (state$.value.data !== undefined || state$.value.error !== false || state$.value.loading !== false) {
            state$.next(initialState);
          }
        }),
      );
    }

    const query$ = using(
      () => load$.subscribe(),
      () => state$,
    );

    const data$ = query$.pipe(
      map(({ data }) => data),
      distinctUntilChanged(),
    );

    return { get: () => data$, getState: () => query$ };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected getTriggersStream(triggers: QueryNotifier[]): Observable<unknown> {
    if (!triggers.length) {
      return EMPTY;
    }
    const observables = triggers.map((trigger) => {
      if (isObservable(trigger)) {
        return trigger;
      }

      return this.eventService.get(trigger);
    });

    return merge(...observables);
  }
}
